import { useEffect, useState, useCallback } from "react";
import ProductFilter from "../../sections/ProductFilter/ProductFilter";
import {
  resetFilters,
  updateFilter,
} from "../../../store/slices/ProductFinder/ProductFinderSlice";
import { useDispatch } from "react-redux";

export default function PromoImageModal({
  isOpen,
  onClose,
  submit,
  update,
  imageUrl,
  labelInit,
  startDateInit,
  endDateInit,
  searchString,
  id,
  hideLabel = false,
  hideFilter = false,
  hideCustomer = false,
  customerNumInit = "",
  isParentNumberInit = false,
  isAftermarketNumberInit = false,
  title = "Add Promotional Image",
}) {
  const [image, setImage] = useState(imageUrl || null);
  const [imageFile, setImageFile] = useState(null);
  const [label, setLabel] = useState(labelInit);
  const [startDate, setStartDate] = useState(startDateInit);
  const [endDate, setEndDate] = useState(endDateInit);
  const [customerNum, setCustomerNum] = useState(customerNumInit || "");
  const [isParentNumber, setIsParentNumber] = useState(isParentNumberInit || false);
  const [isAftermarketNumber, setIsAftermarketNumber] = useState(isAftermarketNumberInit || false);

  const dispatch = useDispatch();

  const handleClose = useCallback(() => {
    setImage(null);
    setImageFile(null);
    setLabel("");
    setStartDate("");
    setEndDate("");
    setCustomerNum("");
    setIsParentNumber(false);
    setIsAftermarketNumber(false);
    dispatch(resetFilters());
    onClose();
  }, [dispatch, onClose]);

  const handleSubmit = useCallback(() => {
    const imageToSubmit = imageFile || image; // Use file object if available, otherwise use existing image URL
    const customerPayload = hideCustomer
      ? {}
      : {
          customer_num: customerNum || null,
          is_parent_number: isParentNumber,
          is_aftermarket_number: isAftermarketNumber,
        };
    if (!id) {
      submit({
        image: imageToSubmit,
        label,
        start_date: startDate,
        end_date: endDate,
        ...customerPayload,
      });
    } else {
      update({
        image: imageToSubmit,
        label,
        start_date: startDate,
        end_date: endDate,
        id,
        ...customerPayload,
      });
    }

    handleClose();
  }, [
    id,
    imageFile,
    image,
    label,
    startDate,
    endDate,
    customerNum,
    isParentNumber,
    isAftermarketNumber,
    hideCustomer,
    submit,
    update,
    handleClose,
  ]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file); // Store the actual file object for backend
      setImage(URL.createObjectURL(file)); // Store URL for preview
    }
  };

  useEffect(() => {
    if (!isOpen || !searchString || hideFilter) return;
    const filters = searchString.split("|");
    dispatch(
      updateFilter({
        types: filters[2]
          ? filters[2].split(":").filter((item) => item.trim() !== "")
          : [],
        manufacturers: filters[0]
          ? filters[0].split(":").filter((item) => item.trim() !== "")
          : [],
        models: filters[1]
          ? filters[1].split(":").filter((item) => item.trim() !== "")
          : [],
      })
    );
  }, [isOpen, searchString, dispatch, hideFilter]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      const tag = e.target.tagName.toLowerCase();
      const isTyping =
        tag === "input" || tag === "textarea" || e.target.isContentEditable;

      if (isTyping) return; // prevent triggering inside inputs

      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "Enter") {
        handleSubmit();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleSubmit, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        className="relative w-full max-w-full sm:max-w-[50%]
 rounded-lg bg-white shadow-xl p-6"
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold"
        >
          ×
        </button>

        <h2 className="text-lg font-semibold text-gray-800 mb-6 text-center">
          {title}
        </h2>
        <div className="flex gap-5">
          <div>
            {/* Close button */}

            {/* Upload field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Image
              </label>
              <div className="flex items-center gap-3">
                <label className="inline-block cursor-pointer !bg-[var(--primary-color)] hover:!bg-[var(--primary-color-light)] text-white text-sm font-medium py-2 px-4 rounded transition">
                  Choose File
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
                <span className="text-sm text-gray-500 truncate max-w-[12rem]">
                  {image ? "File selected" : "No file chosen"}
                </span>
              </div>
              {image && (
                <img
                  src={image}
                  alt="Preview"
                  className="mt-3 h-32 w-auto rounded border object-contain"
                />
              )}
            </div>

            {/* Label field */}
            {!hideLabel && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image Label
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Enter label..."
                  className="block w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                />
              </div>
            )}

            {/* Date selectors */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                />
              </div>
            </div>

            {/* Customer targeting */}
            {!hideCustomer && (
              <div className="mb-4 border border-gray-200 rounded p-3 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Targeting{" "}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="number"
                  value={customerNum}
                  onChange={(e) => setCustomerNum(e.target.value)}
                  placeholder="Customer # (leave blank for all)"
                  className="block w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm mb-3 focus:border-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                />
                {customerNum && (
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isParentNumber}
                        onChange={(e) => {
                          setIsParentNumber(e.target.checked);
                          if (e.target.checked) setIsAftermarketNumber(false);
                        }}
                        className="rounded"
                      />
                      Group image — visible to all customers whose parent # is this customer
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAftermarketNumber}
                        onChange={(e) => {
                          setIsAftermarketNumber(e.target.checked);
                          if (e.target.checked) setIsParentNumber(false);
                        }}
                        className="rounded"
                      />
                      Network image — visible to all customers in this aftermarket network
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
          {!hideFilter && (
            <div>
              <ProductFilter />
            </div>
          )}
        </div>

        {/* Save button */}
        <button
          onClick={() => {
            handleSubmit();
          }}
          className="w-full bg-[var(--primary-color)] hover:bg-[var(--primary-color-light)] text-white font-semibold py-2 rounded transition"
        >
          Save Image
        </button>
      </div>
    </div>
  );
}
